/**
 * 国际化性能优化工具
 * 提供智能预加载、缓存管理和性能监控功能
 */

// 翻译预加载管理器
class I18nPreloader {
    constructor() {
        this.preloadedTranslations = new Set();
        this.loadingQueue = new Map();
        this.performanceMetrics = new Map();
        this.config = {
            // 预加载策略
            preloadStrategy: 'intersection', // 'immediate', 'delayed', 'intersection'
            // 延迟加载时间
            delayedLoadTime: 2000,
            // 交叉观察阈值
            intersectionThreshold: 0.1,
            // 交叉观察边距
            intersectionRootMargin: '300px',
            // 最大并发预加载数
            maxConcurrentPreloads: 3,
            // 缓存时间
            cacheTTL: 10 * 60 * 1000, // 10分钟
        };
    }

    /**
     * 智能预加载翻译资源
     * @param {string} locale 语言代码
     * @param {Array} components 需要预加载的组件列表
     */
    async preloadTranslations(locale, components = []) {
        const startTime = performance.now();
        
        try {
            // 过滤已预加载的组件
            const componentsToLoad = components.filter(comp => 
                !this.preloadedTranslations.has(`${locale}:${comp}`)
            );

            if (componentsToLoad.length === 0) {
                console.log('🎯 All translations already preloaded');
                return;
            }

            // 分批加载，避免阻塞
            const batches = this.chunkArray(componentsToLoad, this.config.maxConcurrentPreloads);
            
            for (const batch of batches) {
                const batchPromises = batch.map(component => 
                    this.preloadComponentTranslation(locale, component)
                );
                
                await Promise.allSettled(batchPromises);
                
                // 批次间延迟，避免阻塞主线程
                if (batches.indexOf(batch) < batches.length - 1) {
                    await this.delay(100);
                }
            }

            const loadTime = performance.now() - startTime;
            console.log(`🎯 Preloaded ${componentsToLoad.length} translations in ${loadTime.toFixed(2)}ms`);
            
            // 记录性能指标
            this.recordPerformanceMetric('preload', loadTime, componentsToLoad.length);
            
        } catch (error) {
            console.error('🎯 Translation preload failed:', error);
        }
    }

    /**
     * 预加载单个组件翻译
     * @param {string} locale 语言代码
     * @param {string} component 组件名称
     */
    async preloadComponentTranslation(locale, component) {
        const cacheKey = `${locale}:${component}`;
        
        if (this.preloadedTranslations.has(cacheKey)) {
            return;
        }

        if (this.loadingQueue.has(cacheKey)) {
            return this.loadingQueue.get(cacheKey);
        }

        const loadPromise = this._loadTranslationFile(locale, component);
        this.loadingQueue.set(cacheKey, loadPromise);

        try {
            await loadPromise;
            this.preloadedTranslations.add(cacheKey);
        } finally {
            this.loadingQueue.delete(cacheKey);
        }
    }

    /**
     * 加载翻译文件
     * @param {string} locale 语言代码
     * @param {string} component 组件名称
     */
    async _loadTranslationFile(locale, component) {
        try {
            // 尝试加载目标语言
            const targetModule = await import(`../../i18n/components/${component}/${locale}.json`);
            
            // 如果不是英文，同时预加载英文兜底
            if (locale !== 'en') {
                const englishModule = await import(`../../i18n/components/${component}/en.json`);
                return {
                    target: targetModule.default,
                    fallback: englishModule.default
                };
            }
            
            return { target: targetModule.default };
        } catch (error) {
            console.warn(`🎯 Failed to preload translation for ${component} in ${locale}:`, error);
            return null;
        }
    }

    /**
     * 基于用户行为的智能预加载
     * @param {string} locale 语言代码
     */
    enableSmartPreloading(locale) {
        // 监听用户滚动，预加载即将可见的组件翻译
        let scrollTimeout;
        const handleScroll = () => {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
                this.preloadVisibleComponents(locale);
            }, 150);
        };

        // 监听用户点击，预加载相关组件翻译
        const handleClick = (event) => {
            const target = event.target.closest('[data-i18n-component]');
            if (target) {
                const component = target.dataset.i18nComponent;
                this.preloadComponentTranslation(locale, component);
            }
        };

        // 监听路由变化，预加载新页面的翻译
        const handleRouteChange = (url) => {
            const routeComponents = this.getRouteComponents(url);
            this.preloadTranslations(locale, routeComponents);
        };

        // 添加事件监听器
        document.addEventListener('scroll', handleScroll, { passive: true });
        document.addEventListener('click', handleClick, { passive: true });
        
        // 返回清理函数
        return () => {
            document.removeEventListener('scroll', handleScroll);
            document.removeEventListener('click', handleClick);
            clearTimeout(scrollTimeout);
        };
    }

    /**
     * 预加载可见组件的翻译
     * @param {string} locale 语言代码
     */
    async preloadVisibleComponents(locale) {
        const visibleComponents = this.getVisibleComponents();
        if (visibleComponents.length > 0) {
            await this.preloadTranslations(locale, visibleComponents);
        }
    }

    /**
     * 获取可见的组件
     * @returns {Array} 可见组件列表
     */
    getVisibleComponents() {
        const components = [];
        const elements = document.querySelectorAll('[data-i18n-component]');
        
        elements.forEach(element => {
            const rect = element.getBoundingClientRect();
            const isVisible = (
                rect.top < window.innerHeight + this.config.intersectionRootMargin &&
                rect.bottom > -this.config.intersectionRootMargin
            );
            
            if (isVisible) {
                const component = element.dataset.i18nComponent;
                if (component && !components.includes(component)) {
                    components.push(component);
                }
            }
        });
        
        return components;
    }

    /**
     * 根据路由获取相关组件
     * @param {string} url 路由URL
     * @returns {Array} 组件列表
     */
    getRouteComponents(url) {
        const routeComponentMap = {
            '/veo3': ['veo3', 'video-generator'],
            '/pix': ['pix', 'videocard'],
            '/pricing': ['pricing'],
            '/content': ['content'],
        };

        for (const [route, components] of Object.entries(routeComponentMap)) {
            if (url.includes(route)) {
                return components;
            }
        }

        return [];
    }

    /**
     * 记录性能指标
     * @param {string} metric 指标名称
     * @param {number} value 指标值
     * @param {number} count 数量
     */
    recordPerformanceMetric(metric, value, count = 1) {
        const key = `${metric}_${new Date().toISOString().split('T')[0]}`;
        const existing = this.performanceMetrics.get(key) || { total: 0, count: 0, avg: 0 };
        
        existing.total += value;
        existing.count += count;
        existing.avg = existing.total / existing.count;
        
        this.performanceMetrics.set(key, existing);
    }

    /**
     * 获取性能报告
     * @returns {Object} 性能报告
     */
    getPerformanceReport() {
        const report = {};
        for (const [key, metrics] of this.performanceMetrics) {
            report[key] = {
                total: metrics.total.toFixed(2),
                count: metrics.count,
                average: metrics.avg.toFixed(2)
            };
        }
        return report;
    }

    /**
     * 清理缓存
     */
    clearCache() {
        this.preloadedTranslations.clear();
        this.loadingQueue.clear();
        console.log('🎯 I18n cache cleared');
    }

    /**
     * 数组分块
     * @param {Array} array 数组
     * @param {number} size 块大小
     * @returns {Array} 分块后的数组
     */
    chunkArray(array, size) {
        const chunks = [];
        for (let i = 0; i < array.length; i += size) {
            chunks.push(array.slice(i, i + size));
        }
        return chunks;
    }

    /**
     * 延迟函数
     * @param {number} ms 延迟毫秒数
     * @returns {Promise} Promise对象
     */
    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}

// 创建全局实例
const i18nPreloader = new I18nPreloader();

// 导出工具函数
export const i18nOptimizer = {
    /**
     * 初始化国际化优化
     * @param {string} locale 当前语言
     */
    init(locale) {
        console.log('🎯 Initializing i18n optimization for locale:', locale);
        
        // 启用智能预加载
        const cleanup = i18nPreloader.enableSmartPreloading(locale);
        
        // 预加载关键组件
        const criticalComponents = ['video-generator', 'pix'];
        i18nPreloader.preloadTranslations(locale, criticalComponents);
        
        return cleanup;
    },

    /**
     * 预加载指定组件的翻译
     * @param {string} locale 语言代码
     * @param {Array} components 组件列表
     */
    preload(locale, components) {
        return i18nPreloader.preloadTranslations(locale, components);
    },

    /**
     * 获取性能报告
     * @returns {Object} 性能报告
     */
    getPerformanceReport() {
        return i18nPreloader.getPerformanceReport();
    },

    /**
     * 清理缓存
     */
    clearCache() {
        i18nPreloader.clearCache();
    }
};

export default i18nPreloader; 